import React, { useEffect, useLayoutEffect, useRef } from "react"
import "twin.macro"
import { graphql } from "gatsby"
import { getImage, withArtDirection } from "gatsby-plugin-image"
import { globalHistory } from "@reach/router"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"

import Layout from "@structure/Layout"
import SectionOne from "@homepage/SectionOne"
import SectionTwo from "@homepage/SectionTwo"
import SectionThree from "@homepage/SectionThree"
import Seo from "@components/Seo"
import OneTrust from "@components/OneTrust";

const IndexPage = ({ data, location }) => {
  gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)

  const sectionOneBgImages = withArtDirection(
    getImage(data.sectionOneBgDesktop),
    [
      {
        media: "(max-width: 1023px)",
        image: getImage(data.sectionOneBgMobile),
      },
    ]
  )

  const sectionTwoNumOneImg = withArtDirection(
    getImage(data.sectionTwoNumOneImgDesktop),
    [
      {
        media: "(max-width: 1023px)",
        image: getImage(data.sectionTwoNumOneImgMobile),
      },
    ]
  )

  const sectionTwoNumTwoImg = withArtDirection(
    getImage(data.howDefencathWorksDesktop),
    [
      {
        media: "(max-width: 1023px)",
        image: getImage(data.howDefencathWorksMobile),
      },
    ]
  )

  const sectionThreeBgImages = withArtDirection(
    getImage(data.sectionThreeDesktop),
    [
      {
        media: "(max-width: 1023px)",
        image: getImage(data.sectionThreeMobile),
      },
      {
        media: "(min-width: 1024px)",
        image: getImage(data.sectionThreeDesktop),
      },
    ]
  )

  const sectionFourFPOVideo = withArtDirection(
    getImage(data.videoThumbnailDesktop),
    [
      {
        media: "(max-width: 1023px)",
        image: getImage(data.videoThumbnailMobile),
      },
    ]
  )

  const sectionOneRef = useRef(null)
  const sectionTwoRef = useRef(null)
  const sectionThreeRef = useRef(null)

  const safetyRef = useRef(null)

  //refs only used on this file
  const section1 = useRef(null)
  const section2 = useRef(null)
  const section3 = useRef(null)
  const inner = useRef(null)

  const handleSection3Styles = (element, style) => {
    element.style.top = style === "fixed" ? "0" : "auto"
    element.style.position = style === "fixed" ? "fixed" : "relative"
    element.style.transform = "translate(0px, 0px)"
  }

  const handleISIStyles = (element, style, position) => {
    element.style.top = style === "fixed" && position === "top" ? "0" : "auto"
    element.style.bottom =
      style === "fixed" && position === "bottom" ? "0" : "auto"
    element.style.position = style === "fixed" ? "fixed" : "relative"
  }

  useEffect(() => {
    const handleKeyPress = e => {
      if (e.key === "Tab") {
        const tanBG = document.querySelector("#resources .tan-background")
        const section3p2 = document.querySelector(
          '[data-screenshot="section-three-part-two"]'
        )

        // For accessibility purposes, when the user navigates the site by the tab key, the page scrolls to the next section
        if (e.target?.parentNode?.parentNode?.id === "how-defencath-works") {
          gsap.to(window, {
            duration: 0.5,
            scrollTo: e.target?.parentNode?.parentNode,
          })
        }
        if (e.target.offsetParent?.classList.contains("safety")) {
          gsap.to(window, {
            duration: 0.5,
            scrollTo:
              section1.current.clientHeight + section2.current.clientHeight,
          })
          gsap.to(tanBG, { transform: "scale(1)", duration: 0.5 })
          section3p2.style.overflow = "hidden"
        }
        if (e.target.offsetParent?.id === "using-defencath") {
          section3p2.style.overflow = "unset"
          gsap.to(tanBG, {
            transform: "scale(3)",
            duration: 0.5,
            ease: "sine.out",
          })
        }
        if (e.target.offsetParent?.id === "main-isi") {
          gsap.to(window, {
            duration: 0.5,
            scrollTo:
              section1.current.clientHeight +
              section2.current.clientHeight +
              section3.current.clientHeight,
          })
        }
      }
    }

    window.addEventListener("keyup", handleKeyPress)

    return () => window.removeEventListener("keyup", handleKeyPress)
  }, [])

  useEffect(() => {
    //makes sure animations are destroyed when navigating to a new page
    return globalHistory.listen(({ action }) => {
      if (action === "PUSH") {
        ScrollTrigger.killAll()
        gsap.globalTimeline.clear()
      }
    })
  }, [])

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: section2.current,
        start: "top top",
        end: "bottom top",
        pin: section3.current,
        pinSpacing: false,
        toggleActions: "restart none none reverse",
        onEnter: () => handleSection3Styles(section3.current, "fixed"),
        onEnterBack: () => handleSection3Styles(section3.current, "fixed"),
        onLeave: () => handleSection3Styles(section3.current, "relative"),
        onLeaveBack: () => handleSection3Styles(section3.current, "relative"),
      })

      ScrollTrigger.create({
        trigger: section1.current,
        start: "70% bottom",
        endTrigger: section2.current,
        end: "center center",
        pin: inner.current,
        pinSpacing: false,
        toggleActions: "restart none none reverse",
      })

      let mm = gsap.matchMedia()

      mm.add(
        {
          isDesktop: "(min-width: 768px) and (min-height: 800px)",
          isSmallHScreen: "(max-height: 799px)",
          isMobile: "(max-width: 767px)",
        },
        context => {
          let { isDesktop, isSmallHScreen, isMobile } = context.conditions

          const ISI = document.getElementById("main-isi")
          const position = isSmallHScreen || isMobile ? "top" : "bottom"

          gsap.to(
            ISI,
            {
              top: isSmallHScreen || isMobile ? "0" : "auto",
              bottom: isDesktop ? "0" : "auto",
              scrollTrigger: {
                trigger: ISI,
                start: "-500px bottom",
                end: isDesktop ? "bottom bottom" : "top top",
                endTrigger: ISI,
                pin: ISI,
                pinSpacing: false,
                pinSpacer: false,
                toggleActions: "restart none none reverse",
                onEnter: () => handleISIStyles(ISI, "fixed", position),
                onEnterBack: () => handleISIStyles(ISI, "fixed", position),
                onLeave: () => handleISIStyles(ISI, "relative", position),
                onLeaveBack: () => handleISIStyles(ISI, "relative", position),
              }
            }
          )

        }
      )

      return () => mm.revert()
    })

    return () => {
      ctx.revert()
      ScrollTrigger.getAll().forEach(st => st.kill())
      ScrollTrigger.refresh()
    }
  }, [])

  return (
    <Layout
      tw="bg-white"
      stickyMenu={true}
      location={location}
      headerTheme="purple"
    >
      <div id="homepage">
        <section tw="relative z-[1] overflow-hidden !top-0" ref={section1}>
          <div className="inner" tw="!top-0" ref={inner}>
            <SectionOne ref={sectionOneRef} bgImg={sectionOneBgImages} />
          </div>
        </section>

        <section
          id="animate-section-2"
          tw="z-[3] h-full overflow-hidden min-h-screen"
          ref={section2}
        >
          <SectionTwo
            ref={sectionTwoRef}
            sectionTwoNumOneImg={sectionTwoNumOneImg}
            sectionTwoNumTwoImg={sectionTwoNumTwoImg}
          />
        </section>
        {/* section 3 trigger -- needs to be outside of the section, due to the way the animation works */}
        <div
          data-isi-trigger="adverse"
          tw="relative top-60 md:(top-80) lg:(top-72)"
        ></div>
        <section
          id="animate-section-3"
          tw="relative z-[2] !top-0 !transform-none overflow-hidden rounded-b-3xl"
          ref={section3}
        >
          <SectionThree
            ref={sectionThreeRef}
            safetySection={safetyRef}
            location={location}
            bgImg={sectionThreeBgImages}
            videoFPOThumbnail={sectionFourFPOVideo}
          />
        </section>
      </div>
    </Layout>
  )
}

export const homepageQuery = graphql`
  query {
    sectionOneBgDesktop: file(
      relativePath: { eq: "homepage/homepage-desktop-top-bkgd.png" }
    ) {
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, quality: 100, placeholder: NONE)
      }
    }
    sectionOneBgMobile: file(
      relativePath: { eq: "homepage/homepage-mobile-top-bkgd.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, quality: 100, placeholder: NONE)
      }
    }
    sectionTwoNumOneImgDesktop: file(
      relativePath: { eq: "homepage/proven-efficacy-desktop.png" }
    ) {
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    sectionTwoNumOneImgMobile: file(
      relativePath: { eq: "homepage/proven-efficacy-mobile.png" }
    ) {
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    howDefencathWorksDesktop: file(
      relativePath: { eq: "homepage/how-defencath-works-desktop.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    howDefencathWorksMobile: file(
      relativePath: { eq: "homepage/how-defencath-works-mobile.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    videoThumbnailDesktop: file(
      relativePath: { eq: "homepage/using-defencath-thumbnail-desktop.png" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    videoThumbnailMobile: file(
      relativePath: { eq: "homepage/using-defencath-thumbnail-mobile.png" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
    sectionThreeDesktop: file(relativePath: { eq: "homepage/bg-three.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    sectionThreeMobile: file(
      relativePath: { eq: "homepage/section-three-mobile.png" }
    ) {
      childImageSharp {
        gatsbyImageData
      }
    }
  }
`

export const Head = () => (
  <>

  <Seo
    title="DefenCath Taurolidine and Heparin CLS | Official Website for HCPs"
    description="DefenCath Taurolidine and Heparin Catheter Lock Solution (CLS) may be appropriate for your adult patients with kidney failure receiving chronic HD-CVC. See Safety and full Prescribing Info."
  />
  {process.env.GATSBY_ONETRUST_TOKEN && (
    <OneTrust otToken={process.env.GATSBY_ONETRUST_TOKEN} />
  )}
    </>
)

export default IndexPage
